// src/components/lazyBlocks.ts
import { EpiBlock } from '@api/types';
import React from 'react';

export type BlockComponentProps = EpiBlock & {
  disableEditPanel?: boolean;
};

export const lazyBlocks: Record<
  string,
  React.LazyExoticComponent<React.ComponentType<BlockComponentProps>>
> = {
  ForgottenPasswordBlock: React.lazy(() =>
    import(
      /* webpackChunkName: "ForgottenPasswordBlock" */ './auth/ForgottenPasswordBlock/ForgottenPasswordBlock'
    ).then((module) => ({
      default: module.ForgottenPasswordBlock,
    }))
  ),
  LoginBlock: React.lazy(() =>
    import(/* webpackChunkName: "LoginBlock" */ './auth/LoginBlock/LoginBlock').then((module) => ({
      default: module.LoginBlock,
    }))
  ),
  ResetPasswordBlock: React.lazy(() =>
    import(
      /* webpackChunkName: "ResetPasswordBlock" */ './auth/ResetPasswordBlock/ResetPasswordBlock'
    ).then((module) => ({
      default: module.ResetPasswordBlock,
    }))
  ),
  BuyersCertificateConsentBlock: React.lazy(() =>
    import(
      /* webpackChunkName: "BuyersCertificateConsentBlock" */ './buyers-certificate/BuyersCertificateConsentBlock'
    ).then((module) => ({
      default: module.BuyersCertificateConsentBlock,
    }))
  ),
  BuyersCertificateCreateBlock: React.lazy(() =>
    import(
      /* webpackChunkName: "BuyersCertificateCreateBlock" */ './buyers-certificate/BuyersCertificateCreateBlock'
    ).then((module) => ({
      default: module.BuyersCertificateCreateBlock,
    }))
  ),
  BuyersCertificateDetailsBlock: React.lazy(() =>
    import(
      /* webpackChunkName: "BuyersCertificateDetailsBlock" */ './buyers-certificate/BuyersCertificateDetailsBlock'
    ).then((module) => ({
      default: module.BuyersCertificateDetailsBlock,
    }))
  ),
  BuyersCertificateListBlock: React.lazy(() =>
    import(
      /* webpackChunkName: "BuyersCertificateListBlock" */ './buyers-certificate/BuyersCertificateListBlock'
    ).then((module) => ({
      default: module.BuyersCertificateListBlock,
    }))
  ),
  BannerBlock: React.lazy(() =>
    import(/* webpackChunkName: "BannerBlock" */ './content/BannerBlock/BannerBlock').then(
      (module) => ({
        default: module.BannerBlock,
      })
    )
  ),
  BrandBlock: React.lazy(() =>
    import(/* webpackChunkName: "BrandBlock" */ './content/BrandBlock/BrandBlock').then(
      (module) => ({
        default: module.BrandBlock,
      })
    )
  ),
  BrochuresBlock: React.lazy(() =>
    import(/* webpackChunkName: "BrochuresBlock" */ './content/BrochuresBlock/BrochuresBlock').then(
      (module) => ({
        default: module.BrochuresBlock,
      })
    )
  ),
  CampaignBlock: React.lazy(() =>
    import(/* webpackChunkName: "CampaignBlock" */ './content/CampaignBlock/CampaignBlock').then(
      (module) => ({
        default: module.CampaignBlock,
      })
    )
  ),
  ColumnBlock: React.lazy(() =>
    import(/* webpackChunkName: "ColumnBlock" */ './content/ColumnBlock/ColumnBlock').then(
      (module) => ({
        default: module.ColumnBlock,
      })
    )
  ),
  ContentBlock: React.lazy(() =>
    import(/* webpackChunkName: "ContentBlock" */ './content/ContentBlock/ContentBlock').then(
      (module) => ({
        default: module.ContentBlock,
      })
    )
  ),
  ContentListBlock: React.lazy(() =>
    import(
      /* webpackChunkName: "ContentListBlock" */ './content/ContentListBlock/ContentListBlock'
    ).then((module) => ({
      default: module.ContentListBlock,
    }))
  ),
  ContentListItemBlock: React.lazy(() =>
    import(
      /* webpackChunkName: "ContentListItemBlock" */ './content/ContentListItemBlock/ContentListItemBlock'
    ).then((module) => ({
      default: module.ContentListItemBlock,
    }))
  ),
  HeroBlock: React.lazy(() =>
    import(/* webpackChunkName: "HeroBlock" */ './content/HeroBlock/HeroBlock').then((module) => ({
      default: module.HeroBlock,
    }))
  ),
  ImageBlock: React.lazy(() =>
    import(/* webpackChunkName: "ImageBlock" */ './content/ImageBlock/ImageBlock').then(
      (module) => ({
        default: module.ImageBlock,
      })
    )
  ),
  ImageListBlock: React.lazy(() =>
    import(/* webpackChunkName: "ImageListBlock" */ './content/ImageListBlock/ImageListBlock').then(
      (module) => ({
        default: module.ImageListBlock,
      })
    )
  ),
  MediaListBlock: React.lazy(() =>
    import(/* webpackChunkName: "MediaListBlock" */ './content/MediaListBlock/MediaListBlock').then(
      (module) => ({
        default: module.MediaListBlock,
      })
    )
  ),
  PageListBlock: React.lazy(() =>
    import(/* webpackChunkName: "PageListBlock" */ './content/PageListBlock/PageListBlock').then(
      (module) => ({
        default: module.PageListBlock,
      })
    )
  ),
  SlideshowBlock: React.lazy(() =>
    import(/* webpackChunkName: "SlideshowBlock" */ './content/SlideshowBlock/SlideshowBlock').then(
      (module) => ({
        default: module.SlideshowBlock,
      })
    )
  ),
  TeaserBlock: React.lazy(() =>
    import(/* webpackChunkName: "TeaserBlock" */ './content/TeaserBlock/TeaserBlock').then(
      (module) => ({
        default: module.TeaserBlock,
      })
    )
  ),
  USPBlock: React.lazy(() =>
    import(/* webpackChunkName: "USPBlock" */ './content/USPBlock/USPBlock').then((module) => ({
      default: module.USPBlock,
    }))
  ),
  CartBlock: React.lazy(() =>
    import(/* webpackChunkName: "CartBlock" */ './checkout/CartBlock/CartBlock').then((module) => ({
      default: module.CartBlock,
    }))
  ),
  CheckoutBlock: React.lazy(() =>
    import(/* webpackChunkName: "CheckoutBlock" */ './checkout/CheckoutBlock/CheckoutBlock').then(
      (module) => ({
        default: module.CheckoutBlock,
      })
    )
  ),
  DealerListBlock: React.lazy(() =>
    import(
      /* webpackChunkName: "DealerListBlock" */ './dealer/DealerListBlock/DealerListBlock'
    ).then((module) => ({
      default: module.DealerListBlock,
    }))
  ),
  DealerDetailsBlock: React.lazy(() =>
    import(
      /* webpackChunkName: "DealerDetailsBlock" */ './dealer/DealerDetailsBlock/DealerDetailsBlock'
    ).then((module) => ({
      default: module.DealerDetailsBlock,
    }))
  ),
  FindDealerBlock: React.lazy(() =>
    import(
      /* webpackChunkName: "FindDealerBlock" */ './dealer/FindDealerBlock/FindDealerBlock'
    ).then((module) => ({
      default: module.FindDealerBlock,
    }))
  ),
  OrderDetailsBlock: React.lazy(() =>
    import(
      /* webpackChunkName: "OrderDetailsBlock" */ './dealer/OrderDetailsBlock/OrderDetailsBlock'
    ).then((module) => ({
      default: module.OrderDetailsBlock,
    }))
  ),
  OrderListBlock: React.lazy(() =>
    import(/* webpackChunkName: "OrderListBlock" */ './dealer/OrderListBlock/OrderListBlock').then(
      (module) => ({
        default: module.OrderListBlock,
      })
    )
  ),
  WishlistBlock: React.lazy(() =>
    import(/* webpackChunkName: "WishlistBlock" */ './dealer/WishlistBlock/WishlistBlock').then(
      (module) => ({
        default: module.WishlistBlock,
      })
    )
  ),
  BreadCrumbsBlock: React.lazy(() =>
    import(
      /* webpackChunkName: "BreadCrumbsBlock" */ './navigation/BreadCrumbsBlock/BreadCrumbsBlock'
    ).then((module) => ({
      default: module.BreadCrumbsBlock,
    }))
  ),
  DropdownMenuBlock: React.lazy(() =>
    import(
      /* webpackChunkName: "DropdownMenuBlock" */ './navigation/DropdownMenuBlock/DropdownMenuBlock'
    ).then((module) => ({
      default: module.DropdownMenuBlock,
    }))
  ),
  FooterBlock: React.lazy(() =>
    import(/* webpackChunkName: "FooterBlock" */ './navigation/FooterBlock/FooterBlock').then(
      (module) => ({
        default: module.FooterBlock,
      })
    )
  ),
  FormContainerBlock: React.lazy(() =>
    import(/* webpackChunkName: "FormContainerBlock" */ './forms/FormContainerBlock').then(
      (module) => ({
        default: module.FormContainerBlock,
      })
    )
  ),
  HeaderBlock: React.lazy(() =>
    import(/* webpackChunkName: "HeaderBlock" */ './navigation/HeaderBlock/HeaderBlock').then(
      (module) => ({
        default: module.HeaderBlock,
      })
    )
  ),
  HeaderLinkBlock: React.lazy(() =>
    import(
      /* webpackChunkName: "HeaderLinkBlock" */ './navigation/HeaderLinkBlock/HeaderLinkBlock'
    ).then((module) => ({
      default: module.HeaderLinkBlock,
    }))
  ),
  LinkBlock: React.lazy(() =>
    import(/* webpackChunkName: "LinkBlock" */ './navigation/LinkBlock/LinkBlock').then(
      (module) => ({
        default: module.LinkBlock,
      })
    )
  ),
  LinkListBlock: React.lazy(() =>
    import(/* webpackChunkName: "LinkListBlock" */ './navigation/LinkListBlock/LinkListBlock').then(
      (module) => ({
        default: module.LinkListBlock,
      })
    )
  ),
  MegaMenuBlock: React.lazy(() =>
    import(/* webpackChunkName: "MegaMenuBlock" */ './navigation/MegaMenuBlock/MegaMenuBlock').then(
      (module) => ({
        default: module.MegaMenuBlock,
      })
    )
  ),
  MenuDrawerBlock: React.lazy(() =>
    import(
      /* webpackChunkName: "MenuDrawerBlock" */ './navigation/MenuDrawerBlock/MenuDrawerBlock'
    ).then((module) => ({
      default: module.MenuDrawerBlock,
    }))
  ),
  NavigationBlock: React.lazy(() =>
    import(
      /* webpackChunkName: "NavigationBlock" */ './navigation/NavigationBlock/NavigationBlock'
    ).then((module) => ({
      default: module.NavigationBlock,
    }))
  ),
  SubmenuBlock: React.lazy(() =>
    import(/* webpackChunkName: "SubmenuBlock" */ './navigation/SubmenuBlock/SubmenuBlock').then(
      (module) => ({
        default: module.SubmenuBlock,
      })
    )
  ),
  SearchBlock: React.lazy(() =>
    import(/* webpackChunkName: "SearchBlock" */ './search/SearchBlock/SearchBlock').then(
      (module) => ({
        default: module.SearchBlock,
      })
    )
  ),
  TechnicalMessageAlertListBlock: React.lazy(() =>
    import(
      /* webpackChunkName: "TechnicalMessageAlertListBlock" */ './technical-message/TechnicalMessageAlertListBlock'
    ).then((module) => ({
      default: module.TechnicalMessageAlertListBlock,
    }))
  ),
  TechnicalMessageCreateBlock: React.lazy(() =>
    import(
      /* webpackChunkName: "TechnicalMessageCreateBlock" */ './technical-message/TechnicalMessageCreateBlock'
    ).then((module) => ({
      default: module.TechnicalMessageCreateBlock,
    }))
  ),
  TechnicalMessageDetailsBlock: React.lazy(() =>
    import(
      /* webpackChunkName: "TechnicalMessageDetailsBlock" */ './technical-message/TechnicalMessageDetailsBlock'
    ).then((module) => ({
      default: module.TechnicalMessageDetailsBlock,
    }))
  ),
  TechnicalMessageListBlock: React.lazy(() =>
    import(
      /* webpackChunkName: "TechnicalMessageListBlock" */ './technical-message/TechnicalMessageListBlock'
    ).then((module) => ({
      default: module.TechnicalMessageListBlock,
    }))
  ),
  WarrantyCreateBlock: React.lazy(() =>
    import(/* webpackChunkName: "WarrantyCreateBlock" */ './warranty/WarrantyCreateBlock').then(
      (module) => ({
        default: module.WarrantyCreateBlock,
      })
    )
  ),
  WarrantyDetailsBlock: React.lazy(() =>
    import(/* webpackChunkName: "WarrantyDetailsBlock" */ './warranty/WarrantyDetailsBlock').then(
      (module) => ({
        default: module.WarrantyDetailsBlock,
      })
    )
  ),
  WarrantyListBlock: React.lazy(() =>
    import(/* webpackChunkName: "WarrantyListBlock" */ './warranty/WarrantyListBlock').then(
      (module) => ({
        default: module.WarrantyListBlock,
      })
    )
  ),
  ProductInfoRequestDetailsBlock: React.lazy(() =>
    import(
      /* webpackChunkName: "ProductInfoRequestDetailsBlock" */ './products/ProductInfoRequestDetailsBlock'
    ).then((module) => ({
      default: module.ProductInfoRequestDetailsBlock,
    }))
  ),
  ProductInfoRequestListBlock: React.lazy(() =>
    import(
      /* webpackChunkName: "ProductInfoRequestListBlock" */ './products/ProductInfoRequestListBlock'
    ).then((module) => ({
      default: module.ProductInfoRequestListBlock,
    }))
  ),
  ProductOrderRequestDetailsBlock: React.lazy(() =>
    import(
      /* webpackChunkName: "ProductOrderRequestDetailsBlock" */ './products/ProductOrderRequestDetailsBlock'
    ).then((module) => ({
      default: module.ProductOrderRequestDetailsBlock,
    }))
  ),
  ProductOrderRequestListBlock: React.lazy(() =>
    import(
      /* webpackChunkName: "ProductOrderRequestListBlock" */ './products/ProductOrderRequestListBlock'
    ).then((module) => ({
      default: module.ProductOrderRequestListBlock,
    }))
  ),
  ProductListBlock: React.lazy(() =>
    import(
      /* webpackChunkName: "ProductListBlock" */ './commerce/ProductListBlock/ProductListBlock'
    ).then((module) => ({
      default: module.ProductListBlock,
    }))
  ),
  ProductListComponent: React.lazy(() =>
    import(
      /* webpackChunkName: "ProductListComponent" */ './commerce/ProductListBlock/ProductListComponent'
    ).then((module) => ({
      default: module.ProductListComponent,
    }))
  ),
  RteBlock: React.lazy(() =>
    import(/* webpackChunkName: "RteBlock" */ './common/RteBlock/RteBlock').then((module) => ({
      default: module.RteBlock,
    }))
  ),
  ImageMedia: React.lazy(() =>
    import(/* webpackChunkName: "ImageMedia" */ './common/ImageMedia/ImageMedia').then(
      (module) => ({
        default: module.ImageMedia,
      })
    )
  ),
  HtmlBlock: React.lazy(() =>
    import(/* webpackChunkName: "HtmlBlock" */ './common/HtmlBlock/HtmlBlock').then((module) => ({
      default: module.HtmlBlock,
    }))
  ),
  ConfirmationBlock: React.lazy(() =>
    import(
      /* webpackChunkName: "ConfirmationBlock" */ './commerce/Confirmation/ConfirmationBlock'
    ).then((module) => ({
      default: module.ConfirmationBlock,
    }))
  ),
  Variation: React.lazy(() =>
    import(/* webpackChunkName: "Variation" */ './commerce/Variation/Variation').then((module) => ({
      default: module.Variation,
    }))
  ),
  ExpandableSubmenu: React.lazy(() =>
    import(
      /* webpackChunkName: "ExpandableSubmenu" */ '../features/ExpandableSubmenu/ExpandableSubmenu'
    ).then((module) => ({
      default: module.ExpandableSubmenu,
    }))
  ),
  MegaMenu: React.lazy(() =>
    import(/* webpackChunkName: "MegaMenu" */ '../features/MegaMenu/MegaMenu').then((module) => ({
      default: module.MegaMenu,
    }))
  ),
  CategoryBlock: React.lazy(() =>
    import(/* webpackChunkName: "CategoryBlock" */ './category/CategoryBlock').then((module) => ({
      default: module.CategoryBlock,
    }))
  ),
  CategoryListBlock: React.lazy(() =>
    import(/* webpackChunkName: "CategoryListBlock" */ './category/CategoryListBlock').then(
      (module) => ({
        default: module.CategoryListBlock,
      })
    )
  ),
};
